import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { authApi } from "../../../Api/authApi";
import { useAuthStore } from "../../../Stores/AuthStore";
import IBeneficiary from "../../../models/beneficiary";
import IContract from "../../../models/contract";

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  beneficiary: IBeneficiary | null;
  contract: IContract | null;
  login: (firstname: string, lastname: string, contractNumber: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const authStore = useAuthStore();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [beneficiary, setBeneficiary] = useState<IBeneficiary | null>(null);
  const [contract, setContract] = useState<IContract | null>(null);

  // Check and load auth data on mount
  useEffect(() => {
    const loadAuthData = async () => {
      if (!authStore.access_token) {
        setIsLoading(false);
        setIsAuthenticated(false);
        return;
      }

      try {
        // Fetch current beneficiary info from JWT
        const { contract: contractData, beneficiary: beneficiaryData } = await authApi.getBeneficiaryMe();

        setContract(contractData);
        setBeneficiary(beneficiaryData);
        setIsAuthenticated(true);
      } catch (error) {
        console.error("Failed to load auth data:", error);
        // Token is invalid or expired
        authStore.logout();
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    loadAuthData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStore.access_token]);

  const login = async (firstname: string, lastname: string, contractNumber: string) => {
    try {
      const response = await authApi.loginBeneficiaryContract(firstname, lastname, contractNumber);

      // Store token
      authStore.setAuth(response.access_token, null);

      // Load beneficiary data
      const { contract: contractData, beneficiary: beneficiaryData } = await authApi.getBeneficiaryMe();
      setContract(contractData);
      setBeneficiary(beneficiaryData);
      setIsAuthenticated(true);

      // Navigate to dashboard
      navigate("/dashboard");
    } catch (error) {
      console.error("Login failed:", error);
      throw new Error("Échec de la connexion. Veuillez vérifier vos informations.");
    }
  };

  const logout = () => {
    // Clear authentication
    authStore.logout();
    setIsAuthenticated(false);
    setBeneficiary(null);
    setContract(null);

    // Navigate to login
    navigate("/");
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        beneficiary,
        contract,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};